import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { KeyRound, ShieldCheck, ShieldX } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { AddTeamMemberPanel } from "@/components/add-team-member-panel";
import { TeamInvitePanel } from "@/components/team-invite-panel";
import { PermissionsMatrix } from "@/components/permissions-matrix";
import { OwnerTransferPanel } from "@/components/owner-transfer-panel";


import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  decideTeamMember,
  listTeamMembers,
  setTeamTwoFactor,
  terminateTeamSessions,
} from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/team")({
  head: () => ({
    meta: [
      { title: "Team management — Quibblies" },
      {
        name: "description",
        content:
          "Owner-only approval of Quibblies team accounts: role assignment, two-factor requirements and session termination.",
      },
      { property: "og:title", content: "Team management — Quibblies" },
      { property: "og:description", content: "Owner-only staff approval and access control." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TeamAdminPage,
});

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-300",
  approved: "bg-emerald-500/15 text-emerald-300",
  suspended: "bg-orange-500/15 text-orange-300",
  revoked: "bg-destructive/15 text-destructive",
};

function TeamAdminPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(listTeamMembers);
  const decide = useServerFn(decideTeamMember);
  const terminate = useServerFn(terminateTeamSessions);
  const toggle2fa = useServerFn(setTeamTwoFactor);

  const [notes, setNotes] = React.useState<Record<string, string>>({});
  const [roles, setRoles] = React.useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-team"],
    queryFn: () => load(),
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-team"] });

  const decideMutation = useMutation({
    mutationFn: (input: { id: string; action: string; role?: string; note?: string }) =>
      decide({ data: input }),
    onSuccess: () => {
      toast.success("Team record updated");
      void refresh();
    },
    onError: (e: Error) => toast.error("Could not update", { description: e.message }),
  });

  const terminateMutation = useMutation({
    mutationFn: (id: string) => terminate({ data: { id } }),
    onSuccess: () => {
      toast.success("All sessions for that account have been ended");
      void refresh();
    },
    onError: (e: Error) => toast.error("Could not end sessions", { description: e.message }),
  });

  const twoFactorMutation = useMutation({
    mutationFn: (input: { id: string; required: boolean }) => toggle2fa({ data: input }),
    onSuccess: () => void refresh(),
    onError: (e: Error) => toast.error("Could not change 2FA rule", { description: e.message }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Team management"
        description="Only the Owner can approve team accounts, assign roles, require two-factor authentication or end an active team session."
      />

      <PermissionsMatrix />

      <TeamInvitePanel />

      <AddTeamMemberPanel />


      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : error ? (
        <div className="mt-8">
          <EmptyState
            icon={ShieldX}
            title="Owner access required"
            description="Team management is restricted to the Quibblies Owner account."
          />
        </div>
      ) : !data?.length ? (
        <div className="mt-8">
          <EmptyState
            icon={ShieldCheck}
            title="No team requests yet"
            description="When someone requests team access, their application appears here for your approval."
          />
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {data.map((member) => (
            <li
              key={member.id}
              className="rounded-2xl border border-border/60 bg-surface/50 p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg text-foreground">{member.displayName}</p>
                  <p className="text-xs text-muted-foreground">@{member.username}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_TONE[member.status] ?? ""}>{member.status}</Badge>
                  <Badge variant="outline">
                    {member.grantedRole ?? `requested ${member.requestedRole}`}
                  </Badge>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <label className="flex items-center gap-2">
                  <Switch
                    checked={member.twoFactorRequired}
                    onCheckedChange={(required) =>
                      twoFactorMutation.mutate({ id: member.id, required })
                    }
                    aria-label={`Require two-factor authentication for ${member.username}`}
                  />
                  Two-factor required
                </label>
                <span>
                  2FA on account: {member.twoFactorEnabled ? "enabled" : "not set up yet"}
                </span>
                {member.sessionsRevokedAt ? (
                  <span>
                    Sessions last ended {new Date(member.sessionsRevokedAt).toLocaleString()}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-[12rem_1fr]">
                <Select
                  value={roles[member.id] ?? member.grantedRole ?? member.requestedRole}
                  onValueChange={(value) => setRoles((r) => ({ ...r, [member.id]: value }))}
                >
                  <SelectTrigger aria-label={`Role for ${member.username}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="administrator">Administrator</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  rows={2}
                  placeholder="Note sent with the decision (optional)"
                  value={notes[member.id] ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [member.id]: e.target.value }))}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {member.status !== "approved" ? (
                  <Button
                    size="sm"
                    variant="hero"
                    disabled={decideMutation.isPending}
                    onClick={() =>
                      decideMutation.mutate({
                        id: member.id,
                        action: member.status === "pending" ? "approve" : "restore",
                        role: roles[member.id] ?? member.grantedRole ?? member.requestedRole,
                        note: notes[member.id] ?? "",
                      })
                    }
                  >
                    {member.status === "pending" ? "Approve & assign role" : "Restore access"}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decideMutation.isPending}
                    onClick={() =>
                      decideMutation.mutate({
                        id: member.id,
                        action: "suspend",
                        note: notes[member.id] ?? "",
                      })
                    }
                  >
                    Suspend
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={decideMutation.isPending}
                  onClick={() =>
                    decideMutation.mutate({
                      id: member.id,
                      action: member.status === "pending" ? "reject" : "revoke",
                      note: notes[member.id] ?? "",
                    })
                  }
                >
                  {member.status === "pending" ? "Decline request" : "Revoke access"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={terminateMutation.isPending}
                  onClick={() => terminateMutation.mutate(member.id)}
                >
                  <KeyRound className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  End all sessions
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <OwnerTransferPanel />
    </div>
  );
}

